import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, CheckCircle2, AlertTriangle, Copy } from 'lucide-react';
import { useApiClient } from '../../lib/apiClient';
import { useI18n } from '../../i18n/LanguageContext';

type ProviderName = 'stripe' | 'mercadopago' | 'manual';

type PaymentSafeView = {
  provider: ProviderName;
  currency: string;
  canProcess: boolean;
  encryptionConfigured: boolean;
  stripe: {
    enabled: boolean;
    publishableKey: string | null;
    secretKeySet: boolean;
    secretKeyHint: string | null;
    webhookSecretSet: boolean;
    webhookUrl: string | null;
  };
  mercadopago: {
    enabled: boolean;
    publicKey: string | null;
    accessTokenSet: boolean;
    accessTokenHint: string | null;
    webhookSecretSet: boolean;
    webhookUrl: string | null;
  };
};

// Two connected processors. Stripe suits MX/BR + global cards; Mercado Pago
// gives the broadest LATAM coverage (cards + PIX/OXXO/boleto/SPEI). One is
// active per clinic, chosen here.
export default function SettingsPayments() {
  const navigate = useNavigate();
  const { fetchJson } = useApiClient();
  const { tr, t: translateKey } = useI18n();

  const [view, setView] = useState<PaymentSafeView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const [provider, setProvider] = useState<'stripe' | 'mercadopago'>('stripe');
  const [currency, setCurrency] = useState('USD');
  // Stripe
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [stripePublishable, setStripePublishable] = useState('');
  const [stripeSecret, setStripeSecret] = useState('');
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
  // Mercado Pago
  const [mpEnabled, setMpEnabled] = useState(false);
  const [mpPublicKey, setMpPublicKey] = useState('');
  const [mpAccessToken, setMpAccessToken] = useState('');
  const [mpWebhookSecret, setMpWebhookSecret] = useState('');

  const applyView = useCallback((v: PaymentSafeView) => {
    setView(v);
    setProvider(v.provider === 'mercadopago' ? 'mercadopago' : 'stripe');
    setCurrency(v.currency || 'USD');
    setStripeEnabled(v.stripe.enabled);
    setStripePublishable(v.stripe.publishableKey || '');
    setStripeSecret('');
    setStripeWebhookSecret('');
    setMpEnabled(v.mercadopago.enabled);
    setMpPublicKey(v.mercadopago.publicKey || '');
    setMpAccessToken('');
    setMpWebhookSecret('');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      applyView(await fetchJson<PaymentSafeView>('/payments/settings'));
    } catch (err: any) {
      setError(err?.message || 'Failed to load payment settings.');
    } finally {
      setLoading(false);
    }
  }, [fetchJson, applyView]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSave() {
    setSaving(true);
    setSuccess(null);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        provider,
        currency: currency.trim().toUpperCase(),
        stripe: {
          enabled: stripeEnabled,
          publishableKey: stripePublishable.trim(),
          ...(stripeSecret.trim() ? { secretKey: stripeSecret.trim() } : {}),
          ...(stripeWebhookSecret.trim() ? { webhookSecret: stripeWebhookSecret.trim() } : {}),
        },
        mercadopago: {
          enabled: mpEnabled,
          publicKey: mpPublicKey.trim(),
          ...(mpAccessToken.trim() ? { accessToken: mpAccessToken.trim() } : {}),
          ...(mpWebhookSecret.trim() ? { webhookSecret: mpWebhookSecret.trim() } : {}),
        },
      };
      applyView(
        await fetchJson<PaymentSafeView>('/payments/settings', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }),
      );
      setSuccess('Saved successfully');
    } catch (err: any) {
      setError(err?.message || 'Failed to save payment settings.');
    } finally {
      setSaving(false);
    }
  }

  function copy(text: string | null) {
    if (text) {
      void navigator.clipboard?.writeText(text);
      setSuccess('Webhook URL copied');
    }
  }

  if (loading) return <div className="p-6">{translateKey('common.loading', 'Loading...')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/settings')} className="p-2 hover:bg-slate-100 rounded-full">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">Payments</h1>
            <p className="text-slate-500 text-sm">Connect a processor to take card payments, deposits, and online invoice payments.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {success && <span className="text-emerald-600 text-sm">{success}</span>}
          <button
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-ink text-white shadow-soft disabled:opacity-60"
            onClick={handleSave}
            disabled={saving}
          >
            <Save size={16} /> {saving ? tr('Saving...') : translateKey('common.save', 'Save')}
          </button>
        </div>
      </div>

      {error && <div className="card p-4 max-w-3xl text-rose-600 text-sm">{error}</div>}

      <div className="max-w-3xl">
        {view?.canProcess ? (
          <div className="flex items-center gap-2 rounded-lg bg-emerald-50 text-emerald-700 px-4 py-3 text-sm">
            <CheckCircle2 size={16} /> Card payments are active via {view.provider === 'mercadopago' ? 'Mercado Pago' : 'Stripe'}.
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-lg bg-amber-50 text-amber-700 px-4 py-3 text-sm">
            <AlertTriangle size={16} /> Card payments are not active yet. Choose a processor, enter its keys, and enable below.
          </div>
        )}
      </div>

      {!view?.encryptionConfigured && (
        <div className="card p-4 max-w-3xl text-amber-700 text-sm">
          Secret storage is not configured on the server (set <code>PAYMENTS_ENCRYPTION_KEY</code>). You can fill in keys, but
          saving secrets will be rejected until that is set.
        </div>
      )}

      {/* Processor selector */}
      <div className="max-w-3xl flex gap-2">
        {(['stripe', 'mercadopago'] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setProvider(p)}
            className={`flex-1 rounded-xl border px-4 py-3 text-left transition ${
              provider === p ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white hover:bg-slate-50'
            }`}
          >
            <div className="font-semibold text-slate-800">{p === 'stripe' ? 'Stripe' : 'Mercado Pago'}</div>
            <div className="text-xs text-slate-500">
              {p === 'stripe' ? 'Global cards · strong in MX/BR' : 'Broad LATAM · cards + PIX/OXXO/boleto/SPEI'}
            </div>
          </button>
        ))}
      </div>

      <div className="card p-6 max-w-3xl space-y-5">
        <Input label="Settlement currency (ISO, e.g. USD, MXN, BRL, ARS)" value={currency} onChange={(v) => setCurrency(v.toUpperCase())} />

        {provider === 'stripe' ? (
          <>
            <Toggle label="Enable Stripe card payments" value={stripeEnabled} onChange={setStripeEnabled} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Input label="Publishable key (pk_…)" value={stripePublishable} onChange={setStripePublishable} placeholder="pk_live_… / pk_test_…" />
              <Input
                label={`Secret key (sk_…)${view?.stripe.secretKeySet ? ` — set: ${view.stripe.secretKeyHint ?? '••••'}` : ''}`}
                value={stripeSecret}
                onChange={setStripeSecret}
                type="password"
                placeholder={view?.stripe.secretKeySet ? 'Leave blank to keep current key' : 'sk_live_… / sk_test_…'}
              />
              <Input
                label={`Webhook signing secret (whsec_…)${view?.stripe.webhookSecretSet ? ' — set' : ''}`}
                value={stripeWebhookSecret}
                onChange={setStripeWebhookSecret}
                type="password"
                placeholder={view?.stripe.webhookSecretSet ? 'Leave blank to keep current secret' : 'whsec_…'}
              />
            </div>
            <WebhookRow url={view?.stripe.webhookUrl ?? null} onCopy={copy} events="checkout.session.completed, charge.refunded" />
          </>
        ) : (
          <>
            <Toggle label="Enable Mercado Pago payments" value={mpEnabled} onChange={setMpEnabled} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Input label="Public key" value={mpPublicKey} onChange={setMpPublicKey} placeholder="APP_USR-… / TEST-…" />
              <Input
                label={`Access token${view?.mercadopago.accessTokenSet ? ` — set: ${view.mercadopago.accessTokenHint ?? '••••'}` : ''}`}
                value={mpAccessToken}
                onChange={setMpAccessToken}
                type="password"
                placeholder={view?.mercadopago.accessTokenSet ? 'Leave blank to keep current token' : 'APP_USR-… access token'}
              />
              <Input
                label={`Webhook secret${view?.mercadopago.webhookSecretSet ? ' — set' : ''}`}
                value={mpWebhookSecret}
                onChange={setMpWebhookSecret}
                type="password"
                placeholder={view?.mercadopago.webhookSecretSet ? 'Leave blank to keep current secret' : 'Signature secret from Tus Integraciones'}
              />
            </div>
            <WebhookRow url={view?.mercadopago.webhookUrl ?? null} onCopy={copy} events="payment notifications (type: payment)" />
          </>
        )}
      </div>
    </div>
  );
}

function WebhookRow({ url, onCopy, events }: { url: string | null; onCopy: (t: string | null) => void; events: string }) {
  return (
    <div className="text-sm space-y-1">
      <span className="text-slate-500">Webhook endpoint (register this URL in your processor dashboard)</span>
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">{url || '—'}</code>
        <button type="button" onClick={() => onCopy(url)} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100" title="Copy">
          <Copy size={16} />
        </button>
      </div>
      <p className="text-xs text-slate-400">Send {events} to this endpoint.</p>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="text-sm space-y-1 block">
      <span className="text-slate-500">{label}</span>
      <input
        className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
        value={value}
        type={type}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-slate-700 font-medium">{label}</span>
    </label>
  );
}
